import { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import LoadingSpinner from '../shared/LoadingSpinner';
import { commonStyles } from '../../theme';

const KeyboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 20px 0;
  border: none;
  max-width: 600px;
  margin: 20px auto;
  touch-action: none;
  background-color: ${commonStyles.primaryBackground};
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 8px;
    max-width: none;
    z-index: 100;
  }
`;

const KeyboardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const KeyboardRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  
  &.middle-row {
    /* Add spacing to center the middle row */
    padding: 0 25px;
    
    @media (max-width: 768px) {
      padding: 0 16px;
    }
  }
`;

const KeyButton = styled.button`
  width: 60px;
  height: 58px;
  background-color: ${commonStyles.keyBackground};
  border: none;
  border-radius: 4px;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${commonStyles.keyText};
  cursor: pointer;
  transition: all 0.15s ease;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;

  &:hover:not(:disabled) {
    background-color: ${commonStyles.keyHover};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
    background-color: var(--key-active);
  }

  &:disabled {
    background-color: var(--key-disabled);
    color: var(--button-disabled-text);
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 50px;
  }
`;

const WideKeyButton = styled(KeyButton)`
  width: 96px;
  font-size: 0.8rem;
  
  @media (max-width: 768px) {
    width: 80px;
    font-size: 0.7rem;
  }
`;

const EnterButton = styled(WideKeyButton)`
  background-color: ${commonStyles.buttonPrimary};
`;

const BackspaceIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H7.07L2.4 12l4.66-7H22v14zm-11.59-2L14 13.41 17.59 17 19 15.59 15.41 12 19 8.41 17.59 7 14 10.59 10.41 7 9 8.41 12.59 12 9 15.59z"/>
  </svg>
);

const Keyboard = ({ 
  currentGuess = '', 
  onLetterPress, 
  onBackspace,
  onSubmitGuess, 
  disabled = false,
  maxLength = 12,
  submittingGuess = false
}) => {

  const firstRow = ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'];
  const secondRow = ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'];
  const thirdRow = ['Z', 'X', 'C', 'V', 'B', 'N', 'M'];

  const handleKeyClick = useCallback((letter) => {
    if (disabled || currentGuess.length >= maxLength) return;
    onLetterPress && onLetterPress(letter);
  }, [disabled, currentGuess.length, maxLength, onLetterPress]);

  const handleSubmit = useCallback(() => {
    if (!disabled && currentGuess.trim()) {
      onSubmitGuess && onSubmitGuess(currentGuess.trim());
    }
  }, [disabled, currentGuess, onSubmitGuess]);

  const handleBackspace = useCallback(() => {
    if (disabled || currentGuess.length === 0) return;
    onBackspace && onBackspace();
  }, [disabled, currentGuess.length, onBackspace]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
  }, []);

  // Handle physical keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (disabled) return;
      
      const key = e.key.toUpperCase();
      
      if (key === 'ENTER') {
        e.preventDefault();
        handleSubmit();
      } else if (key === 'BACKSPACE') {
        e.preventDefault();
        handleBackspace();
      } else if (/^[A-Z]$/.test(key) && currentGuess.length < maxLength) {
        e.preventDefault();
        handleKeyClick(key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [disabled, currentGuess, maxLength, handleBackspace, handleKeyClick, handleSubmit]);

  return (
    <KeyboardContainer>
      <KeyboardGrid>
        {/* First Row */}
        <KeyboardRow>
          {firstRow.map(letter => (
            <KeyButton
              key={letter}
              onTouchStart={handleTouchStart}
              onClick={() => handleKeyClick(letter)}
              disabled={disabled}
              type="button"
            >
              {letter}
            </KeyButton>
          ))}
        </KeyboardRow>
        
        {/* Second Row */}
        <KeyboardRow className="middle-row">
          {secondRow.map(letter => (
            <KeyButton
              key={letter}
              onTouchStart={handleTouchStart}
              onClick={() => handleKeyClick(letter)}
              disabled={disabled}
              type="button"
            >
              {letter}
            </KeyButton>
          ))}
        </KeyboardRow>
        
        {/* Third Row */}
        <KeyboardRow>
          <EnterButton
            onClick={handleSubmit}
            onTouchStart={handleTouchStart}
            disabled={disabled || !currentGuess.trim()}
            type="button"
          >
            {submittingGuess ? <LoadingSpinner size={16} padding="0" /> : 'ENTER'}
          </EnterButton>
          {thirdRow.map(letter => (
            <KeyButton
              key={letter}
              onTouchStart={handleTouchStart}
              onClick={() => handleKeyClick(letter)}
              disabled={disabled}
              type="button"
            >
              {letter}
            </KeyButton>
          ))}
          <WideKeyButton
            onClick={handleBackspace}
            onTouchStart={handleTouchStart}
            disabled={disabled}
            type="button"
          >
            <BackspaceIcon />
          </WideKeyButton>
        </KeyboardRow>
      </KeyboardGrid>
    </KeyboardContainer>
  );
};

export default Keyboard;
